import React, { useState, useEffect, useCallback } from 'react';
import { useStockHistory } from '../context/DataContext';
import './StockHistory.css';

function StockHistory() {
  const { history, loading } = useStockHistory();
  const [filters, setFilters] = useState({
    itemCode: '',
    itemName: '',
    operation: 'all',
    startDate: '',
    endDate: '',
  });
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const applyFilters = useCallback(() => {
    if (!history || history.length === 0) {
      setFilteredHistory([]);
      return;
    }

    let filtered = [...history];

    if (filters.itemCode) {
      filtered = filtered.filter(h => 
        h.itemCode?.toLowerCase().includes(filters.itemCode.toLowerCase())
      );
    }

    if (filters.itemName) {
      filtered = filtered.filter(h => 
        h.itemName?.toLowerCase().includes(filters.itemName.toLowerCase())
      );
    }

    if (filters.operation !== 'all') {
      filtered = filtered.filter(h => h.operation === filters.operation);
    }

    if (filters.startDate) {
      filtered = filtered.filter(h => 
        new Date(h.timestamp) >= new Date(filters.startDate)
      );
    }

    if (filters.endDate) {
      filtered = filtered.filter(h => 
        new Date(h.timestamp) <= new Date(filters.endDate + 'T23:59:59')
      );
    }

    filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setFilteredHistory(filtered);
    setCurrentPage(1);
  }, [filters, history]);

  useEffect(() => {
    // Apply filters when history changes
    if (history && history.length > 0) {
      applyFilters();
    } else {
      setFilteredHistory([]);
    }
  }, [history, filters, applyFilters]);

  const handleFilterChange = (e) => {
    const newFilters = {
      ...filters,
      [e.target.name]: e.target.value,
    };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({
      itemCode: '',
      itemName: '',
      operation: 'all',
      startDate: '',
      endDate: '',
    });
  };

  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredHistory.slice(startIndex, endIndex);

  const getOperationBadge = (operation) => {
    if (operation === 'IN' || operation === 'ADD') {
      return <span className="badge badge-success">➕ IN</span>;
    } else if (operation === 'OUT') {
      return <span className="badge badge-danger">➖ OUT</span>;
    }
    return <span className="badge badge-secondary">{operation}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="history-page">
        <div className="loading"><div className="loading-spinner"></div><p>Loading history...</p></div>
      </div>
    );
  }

  return (
    <div className="history-page">
      <div className="page-header">
        <h1 className="page-title">Stock History</h1>
        <div className="history-count">
          Total Records: <strong>{filteredHistory.length}</strong>
        </div>
      </div>

      <div className="history-filters">
        <div className="filter-row">
          <div className="filter-group">
            <input
              type="text"
              name="itemCode"
              className="form-control"
              placeholder="Filter by Item Code"
              value={filters.itemCode}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filter-group">
            <input
              type="text"
              name="itemName"
              className="form-control"
              placeholder="Filter by Item Name"
              value={filters.itemName}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filter-group">
            <select
              name="operation"
              className="form-control"
              value={filters.operation}
              onChange={handleFilterChange}
            >
              <option value="all">All Operations</option>
              <option value="ADD">Add</option>
              <option value="IN">Stock In</option>
              <option value="OUT">Stock Out</option>
            </select>
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label className="filter-label">From:</label>
            <input
              type="date"
              name="startDate"
              className="form-control"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">To:</label>
            <input
              type="date"
              name="endDate"
              className="form-control"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filter-group filter-actions">
            <button className="btn btn-secondary" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Item Code</th>
              <th>Item Name</th>
              <th>Size</th>
              <th>Operation</th>
              <th>Weight Change</th>
              <th>Packet Change</th>
              <th>Previous Stock</th>
              <th>New Stock</th>
            </tr>
          </thead>
          <tbody>
            {currentData.length === 0 ? (
              <tr><td colSpan="9" className="no-data">No history records found</td></tr>
            ) : (
              currentData.map((record, index) => (
                <tr key={record.id || index}>
                  <td>{formatDate(record.timestamp)}</td>
                  <td><strong>{record.itemCode}</strong></td>
                  <td>{record.itemName}</td>
                  <td>{record.size}</td>
                  <td>{getOperationBadge(record.operation)}</td>
                  <td className={record.weightChange > 0 ? 'positive' : 'negative'}>
                    {record.weightChange > 0 ? '+' : ''}{record.weightChange?.toFixed(2)} g
                  </td>
                  <td className={record.packetChange > 0 ? 'positive' : 'negative'}>
                    {record.packetChange > 0 ? '+' : ''}{record.packetChange}
                  </td>
                  <td>
                    {record.previousWeight?.toFixed(2)} g / {record.previousPackets} packets
                  </td>
                  <td>
                    <strong>
                      {record.newWeight?.toFixed(2)} g / {record.newPackets} packets
                    </strong>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="page-info">Page {currentPage} of {totalPages}</span>
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default StockHistory;